import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { ThinkingIndicatorUI } from '~/design-system/components/PlanningChat/ui/ThinkingIndicatorUI';
import { FeatureDemoScaleFit } from './FeatureDemoScaleFit';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { cn } from '~/lib/utils';
import styles from './FeaturePresentDemo.module.css';

// Figma: Landing-page-V2 "Present" section (nodes 98:5341, 98:7273, 98:7331,
// 108:7666, 108:7697). Prompt → blank slide → generated slide → layout change.
// No camera zoom (the card is already close); the scripted cursor clicks send.
const DESIGN_WIDTH = 682;
const DESIGN_HEIGHT = 580;

const ASSETS = '/landing/features/present-demo';

// Short, zero-context prompts — topic-like, still an ask.
const PROMPT_1 = 'Make a slide about AI and teamwork';
const PROMPT_2 = 'Try a different layout with a circular chart';

const CHAR_MS = 25;
const TYPE_LEAD_MS = 350;
const MOVE_T = { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const };

// Slide slots: generating = slide+pill centered as one group; resting = true
// center; bar present = top slot with the bar below (Figma screen 5).
const SLIDE_H = 338;
const PILL_GAP = 18;
const PILL_H = 44;
const Y_GENERATING = (DESIGN_HEIGHT - (SLIDE_H + PILL_GAP + PILL_H)) / 2; // 90
const Y_RESTING = (DESIGN_HEIGHT - SLIDE_H) / 2; // 121
const Y_TOP = 40;

// Send-button centers (bar x + width - 12 right pad - 14 half button).
const BAR_CENTER = { x: 111, y: 234, w: 460 };
const BAR_BOTTOM = { x: 40, y: 398, w: 602 };
const sendCenter = (bar: typeof BAR_CENTER) => ({ x: bar.x + bar.w - 26, y: bar.y + 86 });

type Phase =
  | 'idle'
  | 'typing'
  | 'click1'
  | 'submit1'
  | 'blank'
  | 'gen1a'
  | 'gen1b'
  | 'v1'
  | 'v1bar'
  | 'typing2'
  | 'click2'
  | 'submit2'
  | 'blank2'
  | 'gen2a'
  | 'gen2b'
  | 'v2'
  | 'reset';

const ORDER: Phase[] = [
  'idle', 'typing', 'click1', 'submit1', 'blank', 'gen1a', 'gen1b', 'v1', 'v1bar',
  'typing2', 'click2', 'submit2', 'blank2', 'gen2a', 'gen2b', 'v2', 'reset',
];

const DURATIONS: Record<Phase, number> = {
  idle: 900,
  typing: TYPE_LEAD_MS + PROMPT_1.length * CHAR_MS + 350,
  click1: 620,
  submit1: 550,
  blank: 800,
  gen1a: 1450,
  gen1b: 1450,
  v1: 1600,
  v1bar: 900,
  typing2: TYPE_LEAD_MS + PROMPT_2.length * CHAR_MS + 350,
  click2: 620,
  submit2: 550,
  blank2: 800,
  gen2a: 1150,
  gen2b: 1150,
  v2: 2600,
  reset: 380,
};

// Narrator pill copy per generating phase (the indicator adds its own dots).
const PILL_TEXTS: Partial<Record<Phase, string>> = {
  blank: 'Sketching the layout',
  gen1a: 'Placing the chart',
  gen1b: 'Writing the notes',
  blank2: 'Rearranging the layout',
  gen2a: 'Moving the notes',
  gen2b: 'Redrawing the chart',
};

const at = (phase: Phase) => ORDER.indexOf(phase);
const between = (p: Phase, from: Phase, to: Phase) => at(p) >= at(from) && at(p) <= at(to);

const BULLETS = [
  '❖ AI suggests layout and tone options',
  '❖ Humans hold creative veto and final authority.',
  "❖ Feedback loops help the system adapt to the creator's style.",
];

const SLIDE_TITLE = 'Human-in-the-Loop Collaboration';

// Debug freeze: ?presentdemo=idle|typing|blank|gen1|v1|v1bar|typing2|gen2|v2
const FREEZE_MAP: Record<string, Phase> = {
  idle: 'idle', typing: 'typing', blank: 'blank', gen1: 'gen1b',
  v1: 'v1', v1bar: 'v1bar', typing2: 'typing2', gen2: 'gen2b', v2: 'v2',
};

const CursorSvg = () => (
  <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 1.5L14.5 9.2L9.3 10.4L12.1 15.9L9.9 17L7.1 11.4L3 14.8V1.5Z"
      fill="#111115"
      stroke="#ffffff"
      strokeWidth="1.2"
    />
  </svg>
);

const SlideV1 = ({ showPanel, showText, animate }: { showPanel: boolean; showText: boolean; animate: boolean }) => (
  <>
    {showPanel && (
      <div className={cn(animate && styles.fadeIn)}>
        <div className={styles.panelMint}>
          <span className={styles.statsLabel}>STATS</span>
          {[39, 84.8, 130.6, 176.4, 222.2].map((y) => (
            <div key={y} className={styles.gridline} style={{ top: y }} />
          ))}
          {[
            { label: '50', top: 34 }, { label: '40', top: 79 }, { label: '30', top: 125 },
            { label: '20', top: 171 }, { label: '10', top: 217 },
          ].map((l) => (
            <span key={l.label} className={styles.yLabel} style={{ top: l.top }}>{l.label}</span>
          ))}
          {[
            { left: 26, top: 126, height: 142, color: '#a390fc', delay: 0 },
            { left: 108.5, top: 79, height: 189, color: '#ec8697', delay: 120 },
            { left: 191, top: 126, height: 142, color: '#f2b84d', delay: 240 },
          ].map((b) => (
            <div
              key={b.left}
              className={cn(styles.bar, animate && styles.barGrow)}
              style={{ left: b.left, top: b.top, height: b.height, backgroundColor: b.color, animationDelay: `${b.delay}ms` }}
            />
          ))}
          {[
            { label: 'AI Suggestions', left: 12, width: 75 },
            { label: 'Human Veto', left: 94, width: 77 },
            { label: 'Style Adaptation', left: 177, width: 75 },
          ].map((l) => (
            <span key={l.label} className={styles.barLabel} style={{ left: l.left, width: l.width }}>
              {l.label}
            </span>
          ))}
        </div>
      </div>
    )}
    {showText && (
      <div className={cn(animate && styles.fadeIn)}>
        <div className={styles.titleV1}>{SLIDE_TITLE}</div>
        <div className={cn(styles.bulletsV1, styles.bullets)}>
          {BULLETS.map((b) => (
            <span key={b} className={styles.bullet}>{b}</span>
          ))}
        </div>
      </div>
    )}
  </>
);

const SlideV2 = ({ showText, showPanel, animate }: { showText: boolean; showPanel: boolean; animate: boolean }) => (
  <>
    {showText && (
      <div className={cn(animate && styles.fadeIn)}>
        <div className={styles.titleV2}>{SLIDE_TITLE}</div>
        <div className={cn(styles.bulletsV2, styles.bullets)}>
          {BULLETS.map((b) => (
            <span key={b} className={styles.bullet}>{b}</span>
          ))}
        </div>
      </div>
    )}
    {showPanel && (
      <div className={cn(animate && styles.fadeIn)}>
        <div className={styles.panelPink}>
          <span className={cn(styles.donutSeg, styles.segPurple)}>
            <img src={`${ASSETS}/donut-seg-purple.svg`} alt="" />
          </span>
          <span className={cn(styles.donutSeg, styles.segPink)}>
            <img src={`${ASSETS}/donut-seg-pink.svg`} alt="" />
          </span>
          <span className={cn(styles.donutSeg, styles.segAmber)}>
            <img src={`${ASSETS}/donut-seg-amber.svg`} alt="" />
          </span>
          <span className={styles.donutValue}>93</span>
          <span className={styles.donutTotal}>Total</span>
          <div className={styles.legend}>
            {[
              { value: '25', color: '#a78bfa', label: 'AI Suggestions' },
              { value: '38', color: '#f471b6', label: 'Human Veto' },
              { value: '30', color: '#f59e0b', label: 'Style Adaptation' },
            ].map((c) => (
              <div key={c.label} className={styles.legendCol}>
                <span className={styles.legendValue} style={{ color: c.color }}>{c.value}</span>
                <span className={styles.legendLabel}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </>
);

const ChatBar = ({ text, typing, sendPressed }: { text: string; typing: boolean; sendPressed: boolean }) => (
  <>
    <div className={styles.chatbarMiddle}>
      {text ? (
        <span className={styles.barText}>
          {text}
          {typing && <span className={styles.caret} />}
        </span>
      ) : (
        <span className={cn(styles.barText, styles.barPlaceholder)}>Explain what you want to show</span>
      )}
    </div>
    <div className={styles.chatbarBottom}>
      <div className={styles.barLeft}>
        <span className={styles.ghostBtn}>
          <DeckIcon name="brand-google" size={16} preserveColors />
          <span className={styles.ghostBtnLabel}>Gemini 3.7 Flash</span>
          <span className={styles.btnChevron}>
            <DeckIcon name="arrow-down-s" size={16} />
          </span>
        </span>
        <span className={styles.ghostBtn}>
          <span className={styles.ghostBtnLabel}>Base theme</span>
          <span className={styles.btnChevron}>
            <DeckIcon name="arrow-down-s" size={16} />
          </span>
        </span>
        <span className={styles.iconBtn28}>
          <DeckIcon name="settings" size={20} />
        </span>
      </div>
      <div className={styles.barRight}>
        <div className={styles.barRightGroup}>
          <span className={styles.iconBtn28}>
            <DeckIcon name="add" size={20} />
          </span>
          <span className={styles.iconBtn28}>
            <DeckIcon name="global" size={20} />
          </span>
        </div>
        <span className={cn(styles.sendBtn, text.length > 0 && styles.sendBtnActive, sendPressed && styles.sendBtnPressed)}>
          <DeckIcon name="arrow-up" size={20} />
        </span>
      </div>
    </div>
  </>
);

export const FeaturePresentDemo = () => {
  const reducedMotion = usePrefersReducedMotion();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);

  const [phase, setPhase] = useState<Phase>('idle');
  const [barText, setBarText] = useState('');
  const [sendPressed, setSendPressed] = useState(false);
  const [frozen, setFrozen] = useState(false);
  const [visible, setVisible] = useState(true);

  // Apply the freeze param post-mount (SSR and first client render must match).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = new URLSearchParams(window.location.search).get('presentdemo');
    const target = raw ? FREEZE_MAP[raw] : undefined;
    if (!target) return;
    setFrozen(true);
    setStarted(true);
    setPhase(target);
    if (target === 'typing') setBarText(PROMPT_1);
    if (target === 'typing2') setBarText(PROMPT_2);
  }, []);

  useEffect(() => {
    if (reducedMotion) return;
    const el = wrapRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setStarted(true);
          observer.unobserve(el);
        }
      },
      { root: null, rootMargin: '0px 0px -10% 0px', threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reducedMotion]);

  // Scripted timeline: advance phases; typing phases schedule per-char ticks;
  // click phases press the send button just before submitting.
  useEffect(() => {
    if (!started || reducedMotion || frozen) return;
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => timers.push(window.setTimeout(fn, ms));

    if (phase === 'typing' || phase === 'typing2') {
      const prompt = phase === 'typing' ? PROMPT_1 : PROMPT_2;
      for (let i = 1; i <= prompt.length; i += 1) {
        later(() => setBarText(prompt.slice(0, i)), TYPE_LEAD_MS + i * CHAR_MS);
      }
    }
    if (phase === 'click1' || phase === 'click2') {
      later(() => setSendPressed(true), 380);
    }
    if (phase === 'submit1' || phase === 'submit2') {
      later(() => setSendPressed(false), 120);
      later(() => setBarText(''), 240);
    }

    const i = ORDER.indexOf(phase);
    const next = ORDER[(i + 1) % ORDER.length];
    later(() => {
      if (phase === 'reset') {
        setVisible(true);
        setBarText('');
      }
      if (phase === 'v2') setVisible(false);
      setPhase(next);
    }, DURATIONS[phase]);

    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [started, reducedMotion, frozen, phase]);

  if (reducedMotion) {
    return (
      <div className={styles.wrap}>
        <FeatureDemoScaleFit designWidth={DESIGN_WIDTH} designHeight={DESIGN_HEIGHT} scaleMode="transform">
          <div className={styles.stage}>
            <div className={styles.slideWrap} style={{ top: Y_RESTING }}>
              <div className={styles.slide}>
                <SlideV1 showPanel showText animate={false} />
              </div>
            </div>
          </div>
        </FeatureDemoScaleFit>
      </div>
    );
  }

  const typingNow = phase === 'typing' || phase === 'typing2';

  const barVisible = between(phase, 'idle', 'submit1') || between(phase, 'v1bar', 'submit2');
  const barBottom = between(phase, 'v1bar', 'submit2');
  const barFading = phase === 'submit1' || phase === 'submit2';
  const bar = barBottom ? BAR_BOTTOM : BAR_CENTER;

  const slideVisible = at(phase) >= at('blank') && phase !== 'reset';
  const generating = between(phase, 'blank', 'gen1b') || between(phase, 'blank2', 'gen2b');
  const slideY = generating ? Y_GENERATING : barBottom ? Y_TOP : Y_RESTING;

  const v1Panel = between(phase, 'gen1a', 'submit2');
  const v1Text = between(phase, 'gen1b', 'submit2');
  const v2Now = at(phase) >= at('gen2a') && phase !== 'reset';
  const v2Text = v2Now;
  const v2Panel = at(phase) >= at('gen2b') && phase !== 'reset';

  const pillText = PILL_TEXTS[phase];
  const clickNow = phase === 'click1' || phase === 'click2';
  const send = sendCenter(bar);

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <FeatureDemoScaleFit designWidth={DESIGN_WIDTH} designHeight={DESIGN_HEIGHT} scaleMode="transform">
        <div className={cn(styles.stage, !visible && styles.stageHidden)}>
          {/* Slide, glow, and narrator pill travel as ONE group — the glow and
              pill are children of the moving wrapper, so they can never arrive
              at a slot before the slide does. */}
          {slideVisible && (
            <motion.div
              className={cn(styles.slideWrap, styles.fadeIn)}
              initial={false}
              animate={{ top: slideY }}
              transition={MOVE_T}
            >
              <div className={styles.slide}>
                {v2Now ? (
                  <SlideV2 showText={v2Text} showPanel={v2Panel} animate={!frozen} />
                ) : (
                  <SlideV1 showPanel={v1Panel} showText={v1Text} animate={!frozen} />
                )}
              </div>
              {generating && <div className={styles.shimmer} />}
              {pillText && (
                <div className={styles.pillDock}>
                  <div className={styles.thinkingCard}>
                    <ThinkingIndicatorUI text={pillText} showLoadingDots />
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Chat bar */}
          {barVisible && (
            <motion.div
              className={styles.chatbar}
              initial={{ x: bar.x, y: bar.y + 36, width: bar.w, opacity: 0 }}
              animate={{
                x: bar.x,
                y: bar.y,
                width: bar.w,
                opacity: barFading ? 0 : 1,
              }}
              transition={MOVE_T}
            >
              <ChatBar text={barText} typing={typingNow} sendPressed={sendPressed} />
            </motion.div>
          )}

          {/* Cursor: appears as typing ends and clicks send */}
          {clickNow && (
            <motion.div
              className={styles.cursor}
              initial={{ x: send.x + 110, y: send.y + 70, opacity: 0 }}
              animate={{ x: send.x - 4, y: send.y - 4, opacity: 1 }}
              transition={{ duration: 0.38, ease: [0.4, 0, 0.2, 1] }}
            >
              <CursorSvg />
            </motion.div>
          )}
        </div>
      </FeatureDemoScaleFit>
    </div>
  );
};

FeaturePresentDemo.displayName = 'FeaturePresentDemo';
